import React, { useEffect, useMemo, useState, useRef } from 'react';
import Modal from '../common/Modal';
import styles from './binary.module.scss';
import { Typography } from '../Typography';
import { Button } from '../Button';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { Tooltip } from 'antd';
import TextInput from '../TextInput/TextInput';
import { queueToast } from '../ToastProvider';

type HostOption = {
  id: string;
  name: string;
  displayName: string;
  icon?: string; // public path to icon (optional)
};

type Package = {
  _id: string;
  name: string;
  product: {
    _id: string;
    product_name: string;
  };
  hostLimit: number;
  hardwareLimit: number;
  price: {
    usd: number;
    eur: number;
  };
  hidden: boolean;
};

type PromotionStep = {
  discountPercentage: number;
  maxOrders: number;
  usedOrders?: number;
};

type PromotionData = {
  _id: string;
  name: string;
  description: string;
  preorderPrice: {
    usd: number;
    eur: number;
  };
  isActive: boolean;
  steps?: PromotionStep[];
};

type Product = {
  _id: string;
  additionalHostPrice?: {
    usd: number;
    eur: number;
  };
  additionalHardwarePrice?: {
    usd: number;
    eur: number;
  };
  product?: {
    additionalHostPrice?: {
      usd: number;
      eur: number;
    };
    additionalHardwarePrice?: {
      usd: number;
      eur: number;
    };
    [key: string]: any;
  };
  [key: string]: any;
};

type BinaryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  topOffset?: number;
  productId: string;
  selectedPackage: Package | null;
};

// Map app name to icon path (aligned with BookPageHome.tsx 243-295)
const getIconSrcForApp = (app: string): string => {
  let iconSrc = '';
  switch (app) {
    case 'DaVinci Resolve':
      iconSrc = '/logo/DaVinci_Resolve_Studio.svg';
      break;
    case 'Adobe Premiere Pro':
      iconSrc = '/logo/Adobe_Premiere_Pro.svg';
      break;
    case 'Sony VEGAS Pro':
      iconSrc = '/logo/Vegas_Pro.svg';
      break;
    case 'Final Cut Pro':
      iconSrc = '/logo/Final_Cut_Pro.svg';
      break;
    case 'Natron':
      iconSrc = '/logo/Natron.svg';
      break;
    case 'Nuke':
      iconSrc = '/logo/Nuke.svg';
      break;
    case 'Apple Motion':
      iconSrc = '/logo/Apple_Motion.svg';
      break;
    default:
      iconSrc = '';
  }
  return iconSrc;
};

type PriceAmounts = {
  usd_amount: number;
  eur_amount: number;
};

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.NEXT_PUBLIC_REACT_APP_BASE_URL1 || '';

async function fetchPrice(
  path: 'base' | 'per-host',
  productId: string,
  signal?: AbortSignal
): Promise<PriceAmounts> {
  const url = `${API_BASE_URL}/price-ops/${path}/${productId}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    throw new Error(`Failed to fetch ${path}: ${res.status} ${errorText}`);
  }
  const json = await res.json();
  if (!json?.data) {
    throw new Error(`Invalid ${path} response`);
  }
  return json.data as PriceAmounts;
}

const BinaryModal: React.FC<BinaryModalProps> = ({
  isOpen,
  onClose,
  topOffset,
  productId,
  selectedPackage,
}) => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [basePrice, setBasePrice] = useState<PriceAmounts | null>(null);
  const [perHostPrice, setPerHostPrice] = useState<PriceAmounts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [hosts, setHosts] = useState<
    Array<{
      _id: string;
      name: string;
      displayName: string;
    }>
  >([]);
  const [hostsLoading, setHostsLoading] = useState(false);
  const toastIdsRef = useRef<string[]>([]);
  const [additionalHosts, setAdditionalHosts] = useState<string>('0');
  const [additionalHardware, setAdditionalHardware] = useState<string>('0');
  const previousStepRef = useRef<1 | 2>(1);
  const [promotion, setPromotion] = useState<PromotionData | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [productLoading, setProductLoading] = useState(false);

  // Default values - no UI selection needed
  const currency: 'eur' = 'eur';

  // Convert hosts from API to HostOption format (flatten OFX subItems)
  const hostOptions: HostOption[] = useMemo(() => {
    let options: HostOption[] = [];

    hosts.forEach((host) => {
      options.push({
        id: host._id,
        name: host.name,
        displayName: host.displayName,
        icon: getIconSrcForApp(host.displayName),
      });
    });
    // remove duplicates
    options = options.filter(
      (option, index, self) => index === self.findIndex((t) => t.id === option.id)
    );

    return options;
  }, [hosts]);

  // Reset flow when modal closes or package changes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(1);
      setHosts([]);
      setSelectedIds([]);
      setError(null);
      setPurchaseLoading(false);
      setAdditionalHosts('0');
      setAdditionalHardware('0');
      setPromotion(null);
      setProduct(null);
      previousStepRef.current = 1;
      // Clear toast IDs ref
      toastIdsRef.current = [];
    }
  }, [isOpen]);

  // Reset to step 1 when package changes
  useEffect(() => {
    if (selectedPackage) {
      setCurrentStep(1);
      setAdditionalHosts('0');
      setAdditionalHardware('0');
      previousStepRef.current = 1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage?._id]);

  const loadPrices = async () => {
    if (!productId) {
      return;
    }

    setLoading(true);
    setError(null);
    const ac = new AbortController();
    try {
      const [base, perHost] = await Promise.all([
        fetchPrice('base', productId, ac.signal),
        fetchPrice('per-host', productId, ac.signal),
      ]);
      setBasePrice(base);
      setPerHostPrice(perHost);
    } catch (e: any) {
      const errorMessage = e?.message || 'Error loading prices. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setBasePrice(null);
      setPerHostPrice(null);
    } finally {
      setLoading(false);
    }
  };
  const loadHosts = async () => {
    if (!productId) {
      return;
    }

    setHostsLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth-token');
      const response = await axios.get(`${API_BASE_URL}/host?product=${productId}`, {
        headers: {
          Authorization: token ? `${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response?.data?.success && response?.data?.data) {
        let hosts = [];
        for (const host of response.data.data) {
          if (host.subItems) {
            for (const subItem of Object.keys(host.subItems)) {
              for (const hostItem of host.subItems[subItem]) {
                hosts.push({
                  _id: hostItem,
                  name: hostItem,
                  displayName: hostItem,
                });
              }
            }
          } else {
            hosts.push(host);
          }
        }

        setHosts(hosts);
      }
    } catch (e: any) {
      console.error('Error loading hosts:', e);
      // Don't set the blocking error state here, as we can still work without OS filtering,
      // but still let the user know the host list failed to load.
      toast.error(e?.response?.data?.message || 'Failed to load available hosts. Please try again.');
    } finally {
      setHostsLoading(false);
    }
  };

  // Load hosts when modal opens (needed for Step 1 max limit and Step 2 selection)
  useEffect(() => {
    if (isOpen && productId) {
      loadHosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, productId]);

  // Fetch product data
  useEffect(() => {
    if (!productId || !isOpen) return;

    let mounted = true;
    const fetchProduct = async () => {
      setProductLoading(true);
      try {
        // Try both endpoints - first try /product/:id (from docs), then fallback to /product-details/:id
        let productData = null;
        try {
          const response = await axios.get(`${API_BASE_URL}/products/${productId}`);
          if (response.data?.data) {
            productData = response.data.data;
          } else if (response.data && !response.data.data) {
            // Response might be direct product object
            productData = response.data;
          }
        } catch (apiError: any) {
          // Fallback to product-details endpoint
          const response = await axios.get(`${API_BASE_URL}/product-details/${productId}`);
          if (response.data?.data) {
            productData = response.data.data;
          }
        }

        if (!mounted) return;

        if (productData) {
          // Handle different response structures:
          // 1. Array: [product]
          // 2. Object with nested product: { product: {...} }
          // 3. Direct product object: {...}
          let actualProduct = null;
          if (Array.isArray(productData)) {
            // If it's an array, take the first element
            actualProduct = productData[0];
          } else if (productData.product) {
            // If it has nested product property
            actualProduct = productData.product;
          } else {
            // Direct product object
            actualProduct = productData;
          }

          setProduct(actualProduct);
        }
      } catch (e: any) {
        if (!mounted) return;
        console.error('Error fetching product:', e);
        setProduct(null);
        toast.error(
          e?.response?.data?.message || 'Failed to load pricing details. Please try again.'
        );
      } finally {
        if (mounted) {
          setProductLoading(false);
        }
      }
    };

    fetchProduct();
    return () => {
      mounted = false;
    };
  }, [productId, isOpen]);

  // Fetch promotion for product
  useEffect(() => {
    if (!productId || !isOpen) return;

    let mounted = true;
    const fetchPromotion = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/promotions/product/${productId}/active`);
        if (!mounted) return;

        // Handle different response formats: { data: {...} } or direct {...} or null
        const promotionData =
          response.data?.data !== undefined ? response.data.data : response.data;

        // If response is null or empty, no active promotion
        if (!promotionData) {
          setPromotion(null);
        } else if (promotionData.isActive) {
          setPromotion(promotionData);
        } else {
          setPromotion(null);
        }
      } catch (e: any) {
        if (!mounted) return;
        // If 404 or no active promotion, set to null (not an error)
        if (e?.response?.status === 404 || e?.response?.status === 400) {
          setPromotion(null);
        } else {
          console.error('Error fetching promotion:', e);
          setPromotion(null);
        }
      }
    };

    fetchPromotion();
    return () => {
      mounted = false;
    };
  }, [productId, isOpen]);

  // Limit additionalHosts when hosts are loaded (use unique hosts count from hostOptions minus package hostLimit)
  useEffect(() => {
    if (hostOptions.length > 0 && additionalHosts && selectedPackage) {
      const currentValue = parseInt(additionalHosts);
      const maxAdditionalHosts = Math.max(0, hostOptions.length - selectedPackage.hostLimit);
      if (!isNaN(currentValue) && currentValue > maxAdditionalHosts) {
        setAdditionalHosts(maxAdditionalHosts.toString());
      }
    }
  }, [hostOptions.length, additionalHosts, selectedPackage]);

  // Clear selected hosts when going back to step 1
  useEffect(() => {
    // If we're going back to step 1 from step 2, clear selections
    const prevStep = previousStepRef.current;
    if (currentStep === 1 && prevStep === 2 && selectedIds.length > 0) {
      setSelectedIds([]);
    }
    // Update previous step ref after checking
    previousStepRef.current = currentStep;
  }, [currentStep, selectedIds.length]);

  // Clear selections when package or additional hosts count changes
  useEffect(() => {
    if (selectedIds.length > 0) {
      setSelectedIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackage?._id, additionalHosts]);

  const toggleSelection = (id: string, maxAllowed: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        // Allow deselection
        return prev.filter((x) => x !== id);
      } else {
        // Only allow selection if under the limit
        if (prev.length < maxAllowed) {
          return [...prev, id];
        }
        return prev;
      }
    });
  };

  // Create order and redirect to Stripe from Step 1
  const handlePurchaseFromStep1 = async () => {
    if (!selectedPackage) return;

    setPurchaseLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth-token');
      if (!token) {
        localStorage.setItem('redirectTo', window.location.pathname + window.location.search);
        onClose();
        window.location.href = '/login';
        return;
      }

      // Get host names - if all hosts are included in package or user selected max additional hosts, send all host names
      let selectedHostNames: string[] = [];
      const additionalHostsCount = parseInt(additionalHosts) || 0;
      const maxAdditionalHosts = Math.max(0, hostOptions.length - selectedPackage.hostLimit);
      const allHostsIncluded =
        hostOptions.length > 0 && hostOptions.length === selectedPackage.hostLimit;

      if (
        allHostsIncluded ||
        (additionalHostsCount === maxAdditionalHosts && maxAdditionalHosts > 0)
      ) {
        // If all hosts are included in package or user selected maximum additional hosts, send all host names
        selectedHostNames = hostOptions.map((option) => option.name);
      }

      const additionalHostsCountFinal = parseInt(additionalHosts) || 0;
      const additionalHardwareCountFinal = parseInt(additionalHardware) || 0;

      const requestBody: any = {
        packageId: selectedPackage._id,
        currency: currency,
        supports: selectedHostNames,
        additionalHosts: additionalHostsCountFinal,
        additionalHardware: additionalHardwareCountFinal,
      };

      // Add promotionId if promotion is active
      if (promotion && promotion.isActive && promotion._id) {
        requestBody.promotionId = promotion._id;
      }

      // Use preorder endpoint only if promotion is active, otherwise use regular order endpoint
      const orderEndpoint =
        promotion && promotion.isActive
          ? `${API_BASE_URL}/order/preorder`
          : `${API_BASE_URL}/order`;

      const response = await axios.post(orderEndpoint, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.success && response.data?.session?.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.session.url;
      } else if (response.data?.success && response.data?.order) {
        // Free order: completed immediately, no Stripe redirect needed.
        // window.location.href is a hard navigation that tears down the JS
        // runtime, so the toast is queued in sessionStorage and flushed by
        // <ToastProvider/> once the destination page mounts.
        queueToast(
          'success',
          "Order placed! We've emailed your confirmation — redirecting you to My Orders."
        );
        window.location.href = '/members-area?tab=myorders';
      } else {
        throw new Error(response.data?.message || 'Failed to create order');
      }
    } catch (error: any) {
      console.error('Error creating order:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Something went wrong while creating your order. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handlePurchase = async () => {
    // Validation
    if (selectedIds.length === 0) {
      toast.error('Please select at least one host.');
      return;
    }

    if (!selectedPackage) {
      toast.error('Please select a package to continue.');
      return;
    }

    setPurchaseLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth-token');
      if (!token) {
        localStorage.setItem('redirectTo', window.location.pathname + window.location.search);
        onClose();
        window.location.href = '/login';
        return;
      }

      // Get host names from selected IDs
      const selectedHostNames = selectedIds
        .map((id) => {
          // If ID contains '-', it's a subItem (format: "hostId-subItem")
          const parts = id.split('-');
          if (parts.length > 1) {
            // For subItems, use the subItem name (the part after first '-')
            return parts.slice(1).join('-');
          } else {
            // For regular hosts, find by _id and get name
            const host = hosts.find((h) => h._id === id);
            return host?.name;
          }
        })
        .filter((name): name is string => !!name);

      const additionalHostsCountFinal = parseInt(additionalHosts) || 0;
      const additionalHardwareCountFinal = parseInt(additionalHardware) || 0;

      const requestBody: any = {
        packageId: selectedPackage._id,
        currency: currency,
        supports: selectedHostNames,
        additionalHosts: additionalHostsCountFinal,
        additionalHardware: additionalHardwareCountFinal,
      };

      // Add promotionId if promotion is active
      if (promotion && promotion.isActive && promotion._id) {
        requestBody.promotionId = promotion._id;
      }

      // Use preorder endpoint only if promotion is active, otherwise use regular order endpoint
      const orderEndpoint =
        promotion && promotion.isActive
          ? `${API_BASE_URL}/order/preorder`
          : `${API_BASE_URL}/order`;

      const response = await axios.post(orderEndpoint, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.success && response.data?.session?.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.session.url;
      } else if (response.data?.success && response.data?.order) {
        // Free order: completed immediately, no Stripe redirect needed.
        // window.location.href is a hard navigation that tears down the JS
        // runtime, so the toast is queued in sessionStorage and flushed by
        // <ToastProvider/> once the destination page mounts.
        queueToast(
          'success',
          "Order placed! We've emailed your confirmation — redirecting you to My Orders."
        );
        window.location.href = '/members-area?tab=myorders';
      } else {
        throw new Error(response.data?.message || 'Failed to create order');
      }
    } catch (error: any) {
      console.error('Error creating order:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Something went wrong while creating your order. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Find the highest step that still has available slots
  const getActiveStep = (promotion: PromotionData | null): PromotionStep | null => {
    if (!promotion || !promotion.steps || promotion.steps.length === 0) {
      return null;
    }

    // Sort steps by discountPercentage descending to find the highest available step
    const sortedSteps = [...promotion.steps].sort(
      (a, b) => b.discountPercentage - a.discountPercentage
    );

    // Find the first step that still has available slots
    for (const step of sortedSteps) {
      const usedOrders = step.usedOrders || 0;
      if (usedOrders < step.maxOrders) {
        return step;
      }
    }

    return null;
  };

  // Calculate Step 1 pricing
  const step1Pricing = useMemo(() => {
    if (!selectedPackage) return null;

    const basePackageOldPrice = selectedPackage.price[currency];
    const basePackageCurrentPrice = selectedPackage.price[currency];

    const additionalHostsCount = additionalHosts ? parseInt(additionalHosts, 10) || 0 : 0;
    const additionalHardwareCount = additionalHardware ? parseInt(additionalHardware, 10) || 0 : 0;

    // Prices per unit (from product API)
    // Check both product.additionalHostPrice and product.product.additionalHostPrice
    const perHostPriceValue =
      product?.additionalHostPrice?.[currency] ??
      product?.additionalHostPrice?.eur ??
      product?.product?.additionalHostPrice?.[currency] ??
      product?.product?.additionalHostPrice?.eur ??
      0;
    const perHardwarePriceValue =
      product?.additionalHardwarePrice?.[currency] ??
      product?.additionalHardwarePrice?.eur ??
      product?.product?.additionalHardwarePrice?.[currency] ??
      product?.product?.additionalHardwarePrice?.eur ??
      0;

    // Calculate base costs (before discount)
    const additionalHostsCostOld = additionalHostsCount * perHostPriceValue;
    const additionalHardwareCostOld = additionalHardwareCount * perHardwarePriceValue;

    // Calculate total base price (package + hosts + hardware)
    const totalBasePrice = basePackageOldPrice + additionalHostsCostOld + additionalHardwareCostOld;

    // Get active step and apply discount if promotion is active
    const activeStep = getActiveStep(promotion);
    let discountPercentage = 0;
    if (promotion && promotion.isActive && activeStep) {
      discountPercentage = activeStep.discountPercentage;
    }

    const discountAmount = (totalBasePrice * discountPercentage) / 100;
    const totalFinalPrice = totalBasePrice - discountAmount;

    // Calculate discounted prices for each item proportionally
    // Distribute discount proportionally based on each item's share of total
    const basePackageFinalPrice =
      totalBasePrice > 0
        ? basePackageOldPrice - (basePackageOldPrice / totalBasePrice) * discountAmount
        : basePackageOldPrice;
    const additionalHostsCostFinal =
      totalBasePrice > 0
        ? additionalHostsCostOld - (additionalHostsCostOld / totalBasePrice) * discountAmount
        : additionalHostsCostOld;
    const additionalHardwareCostFinal =
      totalBasePrice > 0
        ? additionalHardwareCostOld - (additionalHardwareCostOld / totalBasePrice) * discountAmount
        : additionalHardwareCostOld;

    return {
      basePackage: {
        oldPrice: basePackageOldPrice,
        currentPrice: discountPercentage > 0 ? basePackageFinalPrice : basePackageCurrentPrice,
      },
      additionalHosts: {
        oldPrice: additionalHostsCostOld,
        currentPrice: discountPercentage > 0 ? additionalHostsCostFinal : additionalHostsCostOld,
      },
      additionalHardware: {
        oldPrice: additionalHardwareCostOld,
        currentPrice:
          discountPercentage > 0 ? additionalHardwareCostFinal : additionalHardwareCostOld,
      },
      perHostPrice: perHostPriceValue,
      perHardwarePrice: perHardwarePriceValue,
      total: {
        oldPrice: totalBasePrice,
        currentPrice: totalFinalPrice,
      },
      hasDiscount: discountPercentage > 0,
    };
  }, [selectedPackage, additionalHosts, additionalHardware, product, currency, promotion]);

  // Tooltip icon component for inputs
  const HelpTooltipIcon: React.FC<{ title: React.ReactNode }> = ({ title }) => {
    return (
      <Tooltip
        title={title}
        placement="top"
        overlayInnerStyle={{
          backgroundColor: '#2a2a2a',
          maxWidth: '300px',
          zIndex: 10002,
        }}
        getPopupContainer={() => document.body}
        overlayClassName={styles.tooltipOverlay}
        zIndex={10002}
        destroyTooltipOnHide={true}
      >
        <div
          style={{
            position: 'absolute',
            right: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--color-gray-dark-500)',
            pointerEvents: 'auto',
            zIndex: 1,
          }}
        >
          <img src="/help.svg" alt="Help" width="16" height="16" />
        </div>
      </Tooltip>
    );
  };

  // Step 1: Package customization (Figma design)
  const renderStep1 = () => {
    if (!selectedPackage) return null;

    const pricing = step1Pricing;

    return (
      <div className={styles.step1Content}>
        {/* Selected Package Info */}
        <div className={styles.selectedPackageCard}>
          <div className={styles.selectedPackageIcon}>
            <img src="/packageIllustration.svg" alt="Package" />
          </div>
          <div className={styles.selectedPackageText}>
            <div className={styles.selectedPackageTitleRow}>
              <Typography variant="text-sm" color="grayDark-300" weight="medium">
                Selected package:
              </Typography>
              <Typography variant="text-sm" color="grayDark-400">
                {selectedPackage.name}
              </Typography>
            </div>
            <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 4 }}>
              Base configuration includes: {selectedPackage.hostLimit} supported hosts,{' '}
              {selectedPackage.hardwareLimit} development machines
            </Typography>
          </div>
        </div>

        {/* Additional Options */}
        <div className={styles.additionalOptions}>
          {/* Add additional hosts */}
          {(() => {
            const maxAdditionalHosts = Math.max(0, hostOptions.length - selectedPackage.hostLimit);
            // Don't show input if user can't select additional hosts
            if (maxAdditionalHosts === 0) {
              return null;
            }
            return (
              <div className={styles.optionField}>
                <Typography variant="text-sm" color="grayDark-300" weight="medium">
                  Number of additional hosts
                </Typography>
                <TextInput
                  placeholder="Enter number of additional hosts"
                  value={additionalHosts}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    if (value === '') {
                      setAdditionalHosts('');
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        if (maxAdditionalHosts > 0 && numValue > maxAdditionalHosts) {
                          setAdditionalHosts(maxAdditionalHosts.toString());
                        } else {
                          setAdditionalHosts(value);
                        }
                      }
                    }
                  }}
                  fullWidth
                  type="number"
                  max={maxAdditionalHosts > 0 ? maxAdditionalHosts : undefined}
                  helperText={
                    maxAdditionalHosts > 0 && pricing
                      ? `Maximum ${maxAdditionalHosts} additional hosts available (€${
                          productLoading ? '...' : pricing.perHostPrice.toLocaleString()
                        } per host)`
                      : undefined
                  }
                  rightElement={
                    <HelpTooltipIcon
                      title={
                        <div>
                          <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                            Additional Hosts
                          </Typography>
                          <Typography
                            variant="text-sm"
                            color="grayDark-400"
                            style={{ marginTop: 8 }}
                          >
                            Enter the number of additional hosts you need beyond the base package
                            configuration. Each additional host costs €
                            {productLoading
                              ? '...'
                              : pricing?.perHostPrice?.toLocaleString() || '0'}{' '}
                            per year.
                            {maxAdditionalHosts > 0 &&
                              ` Maximum: ${maxAdditionalHosts} additional hosts.`}
                          </Typography>
                        </div>
                      }
                    />
                  }
                />
              </div>
            );
          })()}

          {/* Add additional hardware per OS */}
          <div className={styles.optionField}>
            <Typography variant="text-sm" color="grayDark-300" weight="medium">
              Number of additional development machines
            </Typography>
            <TextInput
              placeholder="Enter number of additional development machines"
              value={additionalHardware}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setAdditionalHardware(value);
              }}
              fullWidth
              type="number"
              rightElement={
                <HelpTooltipIcon
                  title={
                    <div>
                      <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                        Additional Development Machines
                      </Typography>
                      <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 8 }}>
                        Enter the number of additional development machines you need beyond the base
                        package configuration. Each additional development machine costs €
                        {productLoading
                          ? '...'
                          : pricing?.perHardwarePrice?.toLocaleString() || '0'}{' '}
                        per year.
                      </Typography>
                    </div>
                  }
                />
              }
            />
            <Typography variant="text-sm" color="grayDark-400" style={{ marginTop: 8 }}>
              €{productLoading ? '...' : pricing?.perHardwarePrice?.toLocaleString() || '0'} per
              development machine
            </Typography>
          </div>
        </div>

        {/* Price Summary */}
        {pricing && (
          <div className={styles.priceSummary}>
            <Typography
              variant="text-md"
              color="grayDark-50"
              weight="semibold"
              style={{ marginBottom: 16 }}
            >
              Price Summary
            </Typography>

            {/* Base Package */}
            <div className={styles.priceRow}>
              <Typography variant="text-sm" color="grayDark-400">
                Base Package
              </Typography>
              <div className={styles.priceValues}>
                {pricing.hasDiscount &&
                  pricing.basePackage.oldPrice > pricing.basePackage.currentPrice && (
                    <Typography
                      variant="text-sm"
                      color="error"
                      style={{ textDecoration: 'line-through', marginRight: 8 }}
                    >
                      €{Math.round(pricing.basePackage.oldPrice).toLocaleString()}
                    </Typography>
                  )}
                <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                  €{Math.round(pricing.basePackage.currentPrice).toLocaleString()}
                </Typography>
              </div>
            </div>

            {/* Additional Hosts */}
            {(() => {
              const maxAdditionalHosts = Math.max(
                0,
                hostOptions.length - selectedPackage.hostLimit
              );
              // Don't show price row if user can't select additional hosts
              if (maxAdditionalHosts === 0) {
                return null;
              }
              return (
                <div className={styles.priceRow}>
                  <Typography variant="text-sm" color="grayDark-400">
                    Additional supported hosts (€
                    {productLoading ? '...' : pricing.perHostPrice.toLocaleString()} per host)
                  </Typography>
                  <div className={styles.priceValues}>
                    {pricing.hasDiscount &&
                      pricing.additionalHosts.oldPrice > 0 &&
                      pricing.additionalHosts.oldPrice > pricing.additionalHosts.currentPrice && (
                        <Typography
                          variant="text-sm"
                          color="error"
                          style={{ textDecoration: 'line-through', marginRight: 8 }}
                        >
                          €{Math.round(pricing.additionalHosts.oldPrice).toLocaleString()}
                        </Typography>
                      )}
                    <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                      €{Math.round(pricing.additionalHosts.currentPrice).toLocaleString()}
                    </Typography>
                  </div>
                </div>
              );
            })()}

            {/* Additional Hardware */}
            <div className={styles.priceRow}>
              <Typography variant="text-sm" color="grayDark-400">
                Additional development machines (€
                {productLoading ? '...' : pricing.perHardwarePrice.toLocaleString()} per development
                machine)
              </Typography>
              <div className={styles.priceValues}>
                {pricing.hasDiscount &&
                  pricing.additionalHardware.oldPrice > 0 &&
                  pricing.additionalHardware.oldPrice > pricing.additionalHardware.currentPrice && (
                    <Typography
                      variant="text-sm"
                      color="error"
                      style={{ textDecoration: 'line-through', marginRight: 8 }}
                    >
                      €{Math.round(pricing.additionalHardware.oldPrice).toLocaleString()}
                    </Typography>
                  )}
                <Typography variant="text-sm" color="grayDark-50" weight="semibold">
                  €{Math.round(pricing.additionalHardware.currentPrice).toLocaleString()}
                </Typography>
              </div>
            </div>

            {/* Total Price */}
            <div className={styles.priceRowTotal}>
              <Typography variant="text-md" color="grayDark-50" weight="semibold">
                Total price
              </Typography>
              <div className={styles.priceValues}>
                {pricing.hasDiscount && pricing.total.oldPrice > pricing.total.currentPrice && (
                  <Typography
                    variant="text-sm"
                    color="error"
                    style={{ textDecoration: 'line-through', marginRight: 8 }}
                  >
                    €{Math.round(pricing.total.oldPrice).toLocaleString()}
                  </Typography>
                )}
                <Typography
                  variant="text-md"
                  color={pricing.hasDiscount ? 'success' : 'grayDark-50'}
                  weight="semibold"
                >
                  €{Math.round(pricing.total.currentPrice).toLocaleString()}
                </Typography>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Step 2: Host selection (current content)
  const renderStep2 = () => {
    // Calculate max allowed selections: package hostLimit + additional hosts
    const packageHostLimit = selectedPackage?.hostLimit || 0;
    const additionalHostsCount = parseInt(additionalHosts) || 0;
    const maxAllowedSelections = packageHostLimit + additionalHostsCount;

    return (
      <div
        className={purchaseLoading ? styles.contentDisabled : ''}
        aria-disabled={purchaseLoading}
      >
        {error ? <div style={{ marginBottom: 12, color: '#ff6961' }}>{error}</div> : null}
        {maxAllowedSelections > 0 && (
          <Typography variant="text-sm" color="grayDark-400" style={{ marginBottom: 16 }}>
            Select up to {maxAllowedSelections} host{maxAllowedSelections !== 1 ? 's' : ''} (
            {selectedIds.length}/{maxAllowedSelections} selected)
          </Typography>
        )}
        {hostsLoading ? (
          <div className={styles.grid}>
            {[1, 2, 3, 4, 5, 6].map((index) => {
              // Vary text width for more natural look
              const widths = [150, 180, 140, 160, 170, 155];
              return (
                <div key={index} className={styles.hostSkeletonCard}>
                  <div className={styles.hostSkeletonLeft}>
                    <div className={styles.hostSkeletonIcon} />
                    <div
                      className={styles.hostSkeletonText}
                      style={{ width: `${widths[index - 1]}px` }}
                    />
                  </div>
                  <div className={styles.hostSkeletonCheck} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.grid}>
            {hostOptions.map((host) => {
              const selected = selectedIds.includes(host.id);
              const isDisabled = !selected && selectedIds.length >= maxAllowedSelections;
              return (
                <button
                  key={host.id}
                  type="button"
                  className={`${styles.hostCard} ${selected ? styles.selected : ''} ${
                    isDisabled ? styles.disabled : ''
                  }`}
                  onClick={() => toggleSelection(host.id, maxAllowedSelections)}
                  aria-pressed={selected}
                  disabled={purchaseLoading || isDisabled}
                >
                  <div className={styles.hostLeft}>
                    {host.icon ? (
                      <img
                        src={host.icon}
                        alt=""
                        aria-hidden="true"
                        style={{ width: '32px', height: '32px' }}
                      />
                    ) : null}
                    <Typography variant="text-lg" color="grayDark-50" weight="medium">
                      {host.displayName}
                    </Typography>
                  </div>
                  <div className={styles.check} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Get title based on current step
  const getModalTitle = () => {
    if (currentStep === 1) {
      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 12,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
              <img
                src="/modal-background-pattern.svg"
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: -24,
                  left: -24,
                  width: 216,
                  height: 217,
                  opacity: 0.9,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
              <img src="/customIllustration.svg" alt="Customize" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', zIndex: 1 }}>
              <Typography variant="text-lg" color="grayDark-50" weight="semibold">
                Customize your package
              </Typography>
              <Typography variant="text-sm" color="grayDark-400">
                Share where you&apos;ve worked on your profile.
              </Typography>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 12,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
              <img
                src="/modal-background-pattern.svg"
                alt=""
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: -24,
                  left: -24,
                  width: 216,
                  height: 217,
                  opacity: 0.9,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
              <img src="/hostIllustration.svg" alt="Host" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', zIndex: 1 }}>
              <Typography variant="text-lg" color="grayDark-50" weight="semibold">
                Select your host
              </Typography>
              <Typography variant="text-sm" color="grayDark-400">
                Choose your preferred host to generate a custom binary license.
              </Typography>
            </div>
          </div>
        </div>
      );
    }
  };

  // Get footer based on current step
  const getModalFooter = () => {
    if (currentStep === 1) {
      const additionalHostsCount = parseInt(additionalHosts) || 0;
      const maxAdditionalHosts = selectedPackage
        ? Math.max(0, hostOptions.length - selectedPackage.hostLimit)
        : 0;
      const hasSelectedMaxHosts =
        additionalHostsCount === maxAdditionalHosts && maxAdditionalHosts > 0;
      // Skip step 2 if: user selected max hosts, or all hosts are included in package
      const allHostsIncluded =
        selectedPackage &&
        hostOptions.length > 0 &&
        hostOptions.length === selectedPackage.hostLimit;
      const shouldSkipStep2 = hasSelectedMaxHosts || allHostsIncluded;

      return (
        <div className={styles.footerActions}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (shouldSkipStep2) {
                handlePurchaseFromStep1();
              } else {
                setCurrentStep(2);
              }
            }}
            disabled={!selectedPackage}
            loading={purchaseLoading}
          >
            {shouldSkipStep2
              ? promotion && promotion.isActive
                ? `Purchase ${promotion.name}`
                : 'Purchase'
              : 'Continue'}
          </Button>
        </div>
      );
    } else {
      // Calculate max allowed selections for Step 2
      const packageHostLimit = selectedPackage?.hostLimit || 0;
      const additionalHostsCount = parseInt(additionalHosts) || 0;
      const maxAllowedSelections = packageHostLimit + additionalHostsCount;

      return (
        <div className={styles.footerActions}>
          <Button variant="secondary" onClick={() => setCurrentStep(1)}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={handlePurchase}
            disabled={
              selectedIds.length !== maxAllowedSelections ||
              hostsLoading ||
              hosts.length === 0 ||
              purchaseLoading ||
              maxAllowedSelections === 0
            }
            loading={purchaseLoading}
          >
            {promotion && promotion.isActive ? `Purchase ${promotion.name}` : 'Purchase'}
          </Button>
        </div>
      );
    }
  };

  // Don't render if no package selected
  if (!selectedPackage && currentStep === 1) {
    return null;
  }

  return (
    <>
      {/* Toast container with higher z-index than modal - only when modal is open */}
      {isOpen && (
        <Toaster
          position="bottom-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10001,
          }}
          toastOptions={{
            style: {
              zIndex: 10001,
            },
          }}
          gutter={8}
        />
      )}
      <Modal
        isOpen={isOpen && !!selectedPackage}
        onClose={purchaseLoading ? () => {} : onClose}
        topOffset={topOffset}
        maxWidth={880}
        title={getModalTitle()}
        footer={getModalFooter()}
      >
        {currentStep === 1 ? renderStep1() : renderStep2()}
      </Modal>
    </>
  );
};

export default BinaryModal;
